import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import GeneralUtils from '../../shared/utils/GeneralUtils';
import {DELIVERY_WASTE_REASON_CODE, RETURN_REASON_CODE} from '../../shared/utils/Constants';

dayjs.extend(customParseFormat);

export const UNKNOWN_SUPPLIER_KEY = '__UNKNOWN__';
export const UNKNOWN_SUPPLIER_LABEL = 'Unknown Supplier';

export function getItemSupplierId(item) {
    // Selected/override supplier wins for PO grouping & checkout; otherwise current product supplier.
    if (!GeneralUtils.isNullOrEmpty(item?.supplierId)) {
        return item.supplierId;
    }
    return item?.currentSupplierId ?? null;
}

export function getItemSupplierName(item) {
    if (!GeneralUtils.isNullOrEmpty(item?.supplierId)) {
        if (!GeneralUtils.isNullOrEmpty(item?.supplierName)) {
            return item.supplierName;
        }
    }
    if (!GeneralUtils.isNullOrEmpty(item?.currentSupplierName)) {
        return item.currentSupplierName;
    }
    if (!GeneralUtils.isNullOrEmpty(item?.supplierName)) {
        return item.supplierName;
    }
    return null;
}

export function hasSelectedSupplierOverride(item) {
    if (GeneralUtils.isNullOrEmpty(item?.supplierId)) {
        return false;
    }
    if (GeneralUtils.isNullOrEmpty(item?.currentSupplierId)) {
        return true;
    }
    return Number(item.supplierId) !== Number(item.currentSupplierId);
}

export function getSupplierGroupTotalCost(group) {
    const items = group?.items || [];
    let total = 0;
    let hasValue = false;
    items.forEach(item => {
        const qty = Number(item?.actualOrderAmount);
        const cost = Number(item?.currentCost);
        if (!Number.isNaN(qty) && !Number.isNaN(cost)
            && item?.actualOrderAmount !== '' && item?.actualOrderAmount != null
            && item?.currentCost !== '' && item?.currentCost != null) {
            total += qty * cost;
            hasValue = true;
        }
    });
    return hasValue ? total : null;
}

export function formatSupplierTotalCost(total) {
    if (total == null || Number.isNaN(Number(total))) {
        return null;
    }
    return `£${Number(total).toFixed(2)}`;
}

export function findItemIndexInList(itemList, item) {
    const list = itemList || [];
    if (!item) {
        return -1;
    }
    if (item.id != null) {
        const byId = list.findIndex(i => i.id != null && i.id === item.id);
        if (byId >= 0) {
            return byId;
        }
    }
    if (!GeneralUtils.isNullOrEmpty(item.barcode)) {
        const byNewBarcode = list.findIndex(i => !i.id && i.barcode === item.barcode);
        if (byNewBarcode >= 0) {
            return byNewBarcode;
        }
        return list.findIndex(i => i.barcode === item.barcode);
    }
    return -1;
}

export function upsertRequisitionItem(itemList, itemData) {
    const normalizedItem = normalizeItem(itemData);
    const list = [...(itemList || [])];
    let index = findItemIndexInList(list, normalizedItem);
    if (index >= 0) {
        list[index] = {...list[index], ...normalizedItem};
    } else {
        list.push({...normalizedItem, status: normalizedItem.status || 'ACTIVE'});
        index = list.length - 1;
    }
    return {itemList: list, index};
}

export function normalizeItem(item) {
    if (!item) {
        return item;
    }
    const currentSupplierId = item.currentSupplierId ?? null;
    const currentSupplierName = item.currentSupplierName ?? null;
    // supplierId/Name = optional override; do not copy over current supplier.
    const selectedSupplierId = item.supplierId ?? null;
    const selectedSupplierName = item.supplierName ?? null;
    let expiryDate = item.expiryDate || null;
    if (expiryDate && typeof expiryDate === 'string' && expiryDate.includes('T')) {
        expiryDate = dayjs(expiryDate).format('DD/MM/YYYY');
    } else if (expiryDate && /^\d{4}-\d{2}-\d{2}/.test(String(expiryDate))) {
        expiryDate = dayjs(expiryDate).format('DD/MM/YYYY');
    }
    return {
        ...item,
        longName: item.longName || item.itemName || null,
        currentSupplierId,
        currentSupplierName,
        supplierId: selectedSupplierId,
        supplierName: selectedSupplierName,
        supplierBssId: item.supplierBssId ?? null,
        mailSent: item.mailSent === true || item.mailSent === 'true',
        mailToEmail: item.mailToEmail || null,
        mailCcEmail: item.mailCcEmail || null,
        damagedQuantity: item.damagedQuantity ?? null,
        reasonCode: item.reasonCode ?? null,
        description: item.description != null ? String(item.description).slice(0, 50) : null,
        deliveredBoxCount: item.deliveredBoxCount ?? null,
        expiryDate
    };
}

export function buildItemPayload(item) {
    const normalized = normalizeItem(item);
    const {
        merchantBranchId,
        ...rest
    } = normalized;
    let expiryDate = normalized.expiryDate || null;
    if (expiryDate && typeof expiryDate === 'string' && expiryDate.includes('/')) {
        const parsed = dayjs(expiryDate, 'DD/MM/YYYY');
        expiryDate = parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
    }
    return {
        ...rest,
        currentSupplierId: normalized.currentSupplierId,
        currentSupplierName: normalized.currentSupplierName,
        supplierId: normalized.supplierId,
        supplierName: normalized.supplierName,
        supplierBssId: normalized.supplierBssId,
        damagedQuantity: normalized.damagedQuantity,
        reasonCode: normalized.reasonCode,
        description: normalized.description != null ? String(normalized.description).slice(0, 50) : null,
        deliveredBoxCount: normalized.deliveredBoxCount,
        expiryDate
    };
}

export function buildRequisitionPayload(requisition) {
    const payload = {
        id: requisition.id,
        merchantBranchId: Number(localStorage.getItem('merchantBranchId')),
        description: requisition.description,
        requisitionStatus: requisition.requisitionStatus || 'OPEN',
        assignedPurchaseRequisitionId: requisition.assignedPurchaseRequisitionId ?? null,
        supplierId: requisition.supplierId ?? null,
        supplierName: requisition.supplierName ?? null,
        supplierBssId: requisition.supplierBssId ?? null,
        itemList: (requisition.itemList || []).map(buildItemPayload)
    };
    // New Return/DN must start Open; never send Closed from create draft.
    if (!payload.id && (payload.requisitionStatus === 'RETURN' || payload.requisitionStatus === 'DELIVERY_NOTE')) {
        payload.approved = false;
        payload.approvedDate = null;
    }
    return payload;
}

/** Build a new Return draft from DN waste lines (damagedQuantity > 0). */
export function buildReturnDraftFromDeliveryNote(deliveryNote, options = {}) {
    if (!deliveryNote?.id) {
        return null;
    }
    const purchaseOrderItems = options.assignedPurchaseOrder?.itemList
        || options.purchaseOrderItems
        || [];
    const poByBarcode = {};
    (purchaseOrderItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode || poByBarcode[barcode]) {
            return;
        }
        poByBarcode[barcode] = item;
    });

    const wasteItems = (deliveryNote.itemList || [])
        .filter(item => toQtyNumber(item.damagedQuantity) > 0)
        .map(item => {
            const wasteQty = toQtyNumber(item.damagedQuantity);
            const barcode = String(item?.barcode || '').trim();
            const poItem = barcode ? poByBarcode[barcode] : null;
            const supplierId = item.supplierId
                ?? item.currentSupplierId
                ?? poItem?.supplierId
                ?? poItem?.currentSupplierId
                ?? null;
            const supplierName = item.supplierName
                ?? item.currentSupplierName
                ?? poItem?.supplierName
                ?? poItem?.currentSupplierName
                ?? null;
            const supplierBssId = item.supplierBssId
                ?? poItem?.supplierBssId
                ?? null;
            const reasonCode = item.reasonCode || resolveWasteReturnReasonCode(item.expiryDate);
            return normalizeItem({
                id: null,
                barcode: item.barcode,
                itemName: item.itemName,
                itemBssId: item.itemBssId,
                itemUnitMeasure: item.itemUnitMeasure,
                itemSize: item.itemSize,
                boxSize: item.boxSize,
                supplierId,
                supplierName,
                supplierBssId,
                currentCost: item.currentCost ?? poItem?.currentCost ?? null,
                actualOrderAmount: wasteQty,
                damagedQuantity: wasteQty,
                reasonCode,
                description: item.description != null ? String(item.description).slice(0, 50) : null,
                deliveredBoxCount: null,
                expiryDate: reasonCode === 'EXPIRED' ? (item.expiryDate || null) : null
            });
        });
    if (wasteItems.length === 0) {
        return null;
    }
    return {
        id: null,
        description: deliveryNote.requisitionNo
            ? `Return from ${deliveryNote.requisitionNo}`
            : 'Return from Delivery Note',
        requisitionStatus: 'RETURN',
        approved: false,
        approvedDate: null,
        assignedPurchaseRequisitionId: deliveryNote.id,
        assignedPurchaseRequisitionNo: deliveryNote.requisitionNo || null,
        createDate: dayjs().format('DD/MM/YYYY'),
        itemList: wasteItems
    };
}

/** Waste → Return reason: expiry on/before today → EXPIRED, otherwise DAMAGED. */
export function resolveWasteReturnReasonCode(expiryDate) {
    if (!expiryDate) {
        return 'DAMAGED';
    }
    const parsed = typeof expiryDate === 'string' && expiryDate.includes('/')
        ? dayjs(expiryDate, 'DD/MM/YYYY')
        : dayjs(expiryDate);
    if (!parsed.isValid()) {
        return 'DAMAGED';
    }
    return parsed.isAfter(dayjs(), 'day') ? 'DAMAGED' : 'EXPIRED';
}

export function hasWasteItems(requisition) {
    return (requisition?.itemList || []).some(item => toQtyNumber(item.damagedQuantity) > 0);
}

export function hasWasteInComparisonRows(comparisonRows) {
    return (comparisonRows || []).some(row => toQtyNumber(row.damagedQty) > 0);
}

export function shouldOfferCreateReturn(deliveryNote, comparisonRows) {
    return hasWasteItems(deliveryNote) || hasWasteInComparisonRows(comparisonRows);
}

export function hasUnknownSupplierItems(itemList) {
    return (itemList || []).some(item => GeneralUtils.isNullOrEmpty(getItemSupplierId(item)));
}

export function buildCheckoutPayload(requisition, checkoutOptions) {
    const payload = buildRequisitionPayload(requisition);
    const groups = groupItemsBySupplier(requisition.itemList);
    const checkoutDate = dayjs().format('DD/MM/YYYY');
    // Checkout turns document into Purchase Order — emails/PDFs should say PO.
    const pdfRequisition = {
        ...requisition,
        checkoutDate,
        requisitionStatus: 'PURCHASE_ORDER'
    };
    payload.checkoutSupplierOptions = groups.map(group => {
        const options = checkoutOptions[group.key] || {};
        const supplierOption = {
            supplierId: group.supplierId,
            supplierName: group.supplierName,
            mail: options.email === true,
            supplierEmail: options.supplierEmail || '',
            ccEmail: options.ccEmail || '',
            description: requisition.description,
            createDate: requisition.createDate,
            checkoutDate,
            requisitionNo: requisition.requisitionNo,
            requisitionStatus: 'PURCHASE_ORDER'
        };
        if (supplierOption.mail) {
            supplierOption.attachmentBase64 = buildSupplierPdfBase64(group, pdfRequisition);
            supplierOption.attachmentName = buildSupplierAttachmentName(group, pdfRequisition);
        }
        return supplierOption;
    });
    return payload;
}

export function normalizeRequisition(requisition) {
    if (!requisition) {
        return requisition;
    }
    const normalized = Object.assign({}, requisition);
    if (normalized.createDate) {
        normalized.createDate = dayjs(normalized.createDate).format('DD/MM/YYYY');
    }
    if (normalized.checkoutDate) {
        normalized.checkoutDate = dayjs(normalized.checkoutDate).format('DD/MM/YYYY');
    }
    if (normalized.approvedDate) {
        normalized.approvedDate = dayjs(normalized.approvedDate).format('DD/MM/YYYY');
    }
    normalized.approved = normalized.approved === true || normalized.approved === 'true';
    normalized.itemList = (normalized.itemList || []).map(normalizeItem);
    return normalized;
}

export function persistRequisitionToDb(requisition, purchaseRequisitionService) {
    const itemList = requisition.itemList || [];
    if (itemList.length === 0) {
        return Promise.resolve({success: false, reason: 'empty'});
    }
    const payload = buildRequisitionPayload(requisition);
    const apiCall = payload.id
        ? purchaseRequisitionService.update(payload)
        : purchaseRequisitionService.create(payload);
    return apiCall.then(response => {
        if (GeneralUtils.isNullOrEmpty(response) || !GeneralUtils.isNullOrEmpty(response.type)) {
            return {success: false};
        }
        const merged = {
            ...response,
            supplierId: response.supplierId != null ? response.supplierId : requisition.supplierId,
            supplierName: response.supplierName != null ? response.supplierName : requisition.supplierName,
            supplierBssId: response.supplierBssId != null ? response.supplierBssId : requisition.supplierBssId
        };
        return {success: true, requisition: normalizeRequisition(merged)};
    }).catch(() => ({success: false}));
}

export function applyCostWarningsToItems(itemList, warnings) {
    const warningMap = {};
    (warnings || []).forEach(warning => {
        if (warning?.barcode) {
            warningMap[warning.barcode] = warning;
        }
    });
    return (itemList || []).map(item => {
        const warning = warningMap[item.barcode];
        if (!warning) {
            return normalizeItem(item);
        }
        return normalizeItem({
            ...item,
            cheaperCostAvailable: warning.cheaperCostAvailable === true,
            costAlternatives: warning.alternatives || []
        });
    });
}

export function buildCostWarningsMap(warnings) {
    const warningMap = {};
    (warnings || []).forEach(warning => {
        if (warning?.barcode) {
            warningMap[warning.barcode] = warning;
        }
    });
    return warningMap;
}

export function removeItemFromList(itemList, item) {
    const index = findItemIndexInList(itemList, item);
    if (index < 0) {
        return itemList || [];
    }
    return (itemList || []).filter((_, i) => i !== index);
}

const CHECKOUT_REQUIRED_FIELDS = [
    {key: 'barcode', label: 'Barcode', check: (item) => GeneralUtils.isNullOrEmpty(item.barcode)},
    {key: 'itemName', label: 'Product Name', check: (item) => GeneralUtils.isNullOrEmpty(item.itemName)},
    {
        key: 'supplier',
        label: 'Supplier',
        check: (item) => GeneralUtils.isNullOrEmpty(getItemSupplierId(item))
    },
    {
        key: 'actualOrderAmount',
        label: 'Actual Order Amount',
        check: (item) => isActualOrderAmountMissing(item.actualOrderAmount)
    }
];

function isActualOrderAmountMissing(value) {
    if (value == null || value === '') {
        return true;
    }
    if (typeof value === 'string' && value.trim() === '') {
        return true;
    }
    return false;
}

export function formatCheckoutValidationErrors(errors) {
    if (!errors?.length) {
        return null;
    }
    if (errors.length === 1) {
        return errors[0];
    }
    const preview = errors.slice(0, 2).join(' · ');
    return errors.length > 2
        ? `${preview} (${errors.length - 2} more validation error(s))`
        : preview;
}

export function validateItemsForCheckout(itemList) {
    const errors = [];
    groupItemsBySupplier(itemList).forEach(group => {
        const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
        (group.items || []).forEach(rawItem => {
            const item = normalizeItem(rawItem);
            const barcode = item.barcode || '-';
            CHECKOUT_REQUIRED_FIELDS.forEach(field => {
                if (field.check(item)) {
                    errors.push(
                        `In supplier ${supplierName}, for product with barcode ${barcode}, ${field.label} is not entered`
                    );
                }
            });
        });
    });
    return errors;
}

export function buildInitialCheckoutOptions(itemList) {
    const options = {};
    groupItemsBySupplier(itemList).forEach(group => {
        options[group.key] = {
            supplierName: group.supplierName,
            supplierId: group.supplierId,
            print: false,
            email: false,
            supplierEmail: '',
            ccEmail: getLoggedInUserEmail()
        };
    });
    return options;
}

export function getRequisitionDocLabels(requisitionStatus, {forDetailPage = false, forMobile = false} = {}) {
    const detailSuffix = forDetailPage ? ' Detail' : '';
    const isReturn = requisitionStatus === 'RETURN';
    const isDelivery = requisitionStatus === 'DELIVERY_NOTE';
    const isPurchaseOrder = requisitionStatus === 'PURCHASE_ORDER';

    if (isReturn) {
        return {
            docTitle: `Return${detailSuffix}`,
            docNoLabel: 'Return No'
        };
    }
    if (isDelivery) {
        return {
            docTitle: `Delivery Note${detailSuffix}`,
            docNoLabel: 'Delivery Note No'
        };
    }
    if (isPurchaseOrder) {
        return {
            docTitle: `Purchase Order${detailSuffix}`,
            docNoLabel: 'Purchase Order No'
        };
    }
    const defaultTitle = forMobile ? 'Requisition' : 'Purchase Requisition';
    return {
        docTitle: `${defaultTitle}${detailSuffix}`,
        docNoLabel: 'Requisition No'
    };
}

export function buildSupplierAttachmentName(group, requisition) {
    const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
    const safeName = supplierName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_') || 'Supplier';
    const status = requisition?.requisitionStatus;
    let prefix = 'PurchaseRequisition';
    if (status === 'RETURN') {
        prefix = 'Return';
    } else if (status === 'PURCHASE_ORDER' || status === 'DELIVERY_NOTE') {
        prefix = status === 'DELIVERY_NOTE' ? 'DeliveryNote' : 'PurchaseOrder';
    }
    return `${prefix}_${safeName}.pdf`;
}

export function buildSupplierPdfBase64(group, requisition) {
    const doc = new jsPDF('portrait', 'pt', 'A4');
    appendSupplierPdfPage(doc, group, requisition, true);
    const dataUri = doc.output('datauristring');
    const commaIndex = dataUri.indexOf(',');
    return commaIndex >= 0 ? dataUri.substring(commaIndex + 1) : dataUri;
}

export function getLoggedInUserEmail() {
    try {
        const hsmUser = JSON.parse(localStorage.getItem('hsmUser'));
        return hsmUser?.email || '';
    } catch (e) {
        return '';
    }
}

export function isValidEmailAddress(email) {
    if (GeneralUtils.isNullOrEmpty(email)) {
        return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
}

export function validateCheckoutEmailOptions(checkoutOptions) {
    const errors = [];
    Object.keys(checkoutOptions || {}).forEach(groupKey => {
        const options = checkoutOptions[groupKey];
        if (!options?.email) {
            return;
        }
        const supplierName = options.supplierName || UNKNOWN_SUPPLIER_LABEL;
        if (!isValidEmailAddress(options.supplierEmail)) {
            errors.push(`Please enter a valid To email for ${supplierName}`);
        }
        if (!isValidEmailAddress(options.ccEmail)) {
            errors.push(`Please enter a valid CC email for ${supplierName}`);
        }
    });
    return errors;
}

export async function sendRequisitionSupplierEmail(group, requisition, checkoutOption, purchaseRequisitionService) {
    if (!checkoutOption?.email) {
        return {success: true};
    }
    if (!requisition?.id) {
        return {success: false};
    }
    const payload = {
        supplierId: group.supplierId,
        supplierName: group.supplierName,
        supplierEmail: checkoutOption.supplierEmail,
        ccEmail: checkoutOption.ccEmail,
        description: requisition.description,
        createDate: requisition.createDate,
        requisitionNo: requisition.requisitionNo,
        requisitionStatus: requisition.requisitionStatus,
        attachmentBase64: buildSupplierPdfBase64(group, requisition),
        attachmentName: buildSupplierAttachmentName(group, requisition)
    };
    const response = await purchaseRequisitionService.sendSupplierEmail(requisition.id, payload);
    if (!response || response.ok === false || !GeneralUtils.isNullOrEmpty(response.type)) {
        return {success: false};
    }
    return {success: true};
}

export function wasSupplierEmailSent(requisition, group) {
    const status = requisition?.requisitionStatus;
    if (status !== 'PURCHASE_ORDER' && status !== 'RETURN') {
        return false;
    }
    return (group.items || []).some(item => item?.mailSent === true || item?.mailSent === 'true');
}

export function getSupplierMailDetails(group) {
    const item = (group.items || []).find(entry =>
        !GeneralUtils.isNullOrEmpty(entry?.mailToEmail) || !GeneralUtils.isNullOrEmpty(entry?.mailCcEmail)
    );
    if (!item) {
        return null;
    }
    return {
        to: item.mailToEmail || '',
        cc: item.mailCcEmail || ''
    };
}

export function getSupplierGroupKey(item) {
    const supplierId = getItemSupplierId(item);
    if (!GeneralUtils.isNullOrEmpty(supplierId)) {
        return String(supplierId);
    }
    const supplierName = getItemSupplierName(item);
    if (!GeneralUtils.isNullOrEmpty(supplierName)) {
        return supplierName;
    }
    return UNKNOWN_SUPPLIER_KEY;
}

export function getSupplierGroupLabel(item) {
    const supplierName = getItemSupplierName(item);
    return supplierName || UNKNOWN_SUPPLIER_LABEL;
}

export function groupItemsBySupplier(itemList) {
    const groups = new Map();
    (itemList || []).forEach(rawItem => {
        const item = normalizeItem(rawItem);
        const key = getSupplierGroupKey(item);
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                supplierId: getItemSupplierId(item),
                supplierName: getSupplierGroupLabel(item),
                supplierBssId: item.supplierBssId,
                items: []
            });
        }
        groups.get(key).items.push(item);
    });
    return Array.from(groups.values()).sort((a, b) =>
        (a.supplierName || '').localeCompare(b.supplierName || '', undefined, {sensitivity: 'base'})
    );
}

export function getItemsInSupplierGroupOrder(itemList) {
    return groupItemsBySupplier(itemList).flatMap(group => group.items);
}

export function getNextItemInSupplierOrder(itemList, currentItem) {
    const orderedItems = getItemsInSupplierGroupOrder(itemList);
    const currentIndex = findItemIndexInList(orderedItems, currentItem);
    if (currentIndex < 0 || currentIndex + 1 >= orderedItems.length) {
        return null;
    }
    return orderedItems[currentIndex + 1];
}

function formatValue(value) {
    if (value == null || value === '') {
        return '';
    }
    return String(value);
}

const PDF_TABLE_HEAD = [[
    'Barcode',
    'Name',
    'Units per Box',
    'Number of Boxes',
    'Unit Cost',
    'Order Qty'
]];

const PDF_DELIVERY_TABLE_HEAD = [[
    'Barcode',
    'Name',
    'Units/Box',
    'Boxes',
    'Qty',
    'Waste',
    'Net Qty',
    'Reason',
    'Description'
]];

const PDF_RETURN_TABLE_HEAD = [[
    'Barcode',
    'Name',
    'Return Qty',
    'Reason'
]];

function formatReasonCode(reasonCode) {
    if (!reasonCode) {
        return '';
    }
    const match = (DELIVERY_WASTE_REASON_CODE || []).find(option => option.value === reasonCode)
        || (RETURN_REASON_CODE || []).find(option => option.value === reasonCode);
    if (match?.name) {
        return match.name;
    }
    return String(reasonCode).replace(/_/g, ' ');
}

function formatPdfCost(value) {
    if (value == null || value === '') {
        return '';
    }
    const number = Number(value);
    if (Number.isNaN(number)) {
        return String(value);
    }
    return number.toFixed(2);
}

function mapItemsToPdfBody(items, mode = 'default') {
    return (items || []).map(item => {
        const longName = item.longName || item.itemNameWithSizeAndQuantity || item.itemName;
        if (mode === 'return') {
            return [
                formatValue(item.barcode),
                formatValue(longName),
                formatValue(item.actualOrderAmount),
                formatReasonCode(item.reasonCode)
            ];
        }
        if (mode === 'delivery') {
            const wasteQty = item.damagedQuantity;
            const hasWaste = wasteQty != null && String(wasteQty).trim() !== '' && Number(wasteQty) !== 0;
            const deliveredQty = toQtyNumber(item.actualOrderAmount);
            const damagedQty = toQtyNumber(wasteQty);
            const netQty = deliveredQty - damagedQty;
            return [
                formatValue(item.barcode),
                formatValue(longName),
                formatValue(item.boxSize),
                formatValue(item.deliveredBoxCount),
                formatValue(item.actualOrderAmount),
                formatValue(hasWaste ? wasteQty : ''),
                formatValue(netQty),
                formatValue(hasWaste ? formatReasonCode(item.reasonCode) : ''),
                formatValue(hasWaste ? (item.description || '') : '')
            ];
        }
        return [
            formatValue(item.barcode),
            formatValue(longName),
            formatValue(item.boxSize),
            formatValue(item.deliveredBoxCount),
            formatPdfCost(item.currentCost),
            formatValue(item.actualOrderAmount)
        ];
    });
}

export function exportSupplierPdf(group, requisition) {
    const doc = new jsPDF('portrait', 'pt', 'A4');
    appendSupplierPdfPage(doc, group, requisition, true);
    doc.save(buildSupplierAttachmentName(group, requisition));
}

export function exportBulkSupplierPdf(groups, requisition) {
    const list = (groups || []).filter(group => (group?.items || []).length > 0);
    if (list.length === 0) {
        return;
    }
    const doc = new jsPDF('portrait', 'pt', 'A4');
    list.forEach((group, index) => {
        appendSupplierPdfPage(doc, group, requisition, index === 0);
    });
    const docNo = requisition?.requisitionNo || 'document';
    const status = requisition?.requisitionStatus || 'PR';
    doc.save(`${status}_${docNo}_bulk.pdf`);
}

function appendSupplierPdfPage(doc, group, requisition, isFirstPage) {
    if (!isFirstPage) {
        doc.addPage();
    }
    const marginLeft = 40;
    const supplierName = group.supplierName || UNKNOWN_SUPPLIER_LABEL;
    const requisitionNo = requisition?.requisitionNo || '';
    const orderDate = requisition?.checkoutDate || requisition?.approvedDate || '';
    const status = requisition?.requisitionStatus;
    const isReturn = status === 'RETURN';
    const isDelivery = status === 'DELIVERY_NOTE';
    const {docTitle: title, docNoLabel: noLabel} = getRequisitionDocLabels(status);
    const dateLabel = isReturn || isDelivery ? 'Date' : 'Order Date';

    doc.setFontSize(14);
    doc.text(title, marginLeft, 40);
    doc.setFontSize(11);
    let headerY = 58;
    if (requisitionNo) {
        doc.text(`${noLabel}: ${requisitionNo}`, marginLeft, headerY);
        headerY += 16;
    }
    doc.text(`Supplier: ${supplierName}`, marginLeft, headerY);
    headerY += 16;
    if (orderDate) {
        doc.text(`${dateLabel}: ${orderDate}`, marginLeft, headerY);
        headerY += 16;
    }

    const tableMode = isReturn ? 'return' : (isDelivery ? 'delivery' : 'default');
    const tableHead = isReturn
        ? PDF_RETURN_TABLE_HEAD
        : (isDelivery ? PDF_DELIVERY_TABLE_HEAD : PDF_TABLE_HEAD);

    doc.autoTable({
        startY: headerY + 8,
        head: tableHead,
        body: mapItemsToPdfBody(group.items, tableMode),
        margin: {left: marginLeft, right: marginLeft},
        styles: isDelivery ? {fontSize: 8, cellPadding: 3} : undefined,
        headStyles: isDelivery ? {fontSize: 8} : undefined
    });
}

function toQtyNumber(value) {
    if (value == null || value === '') {
        return 0;
    }
    const number = Number(value);
    return Number.isNaN(number) ? 0 : number;
}

/**
 * Compare Purchase Order ordered items vs Delivery Note received items by barcode.
 */
export function buildDeliveryPoComparisonRows(purchaseOrderItems, deliveryNoteItems) {
    const poByBarcode = {};
    (purchaseOrderItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!poByBarcode[barcode]) {
            poByBarcode[barcode] = {
                barcode,
                itemName: item.itemName || '',
                orderedQty: 0
            };
        }
        poByBarcode[barcode].orderedQty += toQtyNumber(item.actualOrderAmount);
        if (!poByBarcode[barcode].itemName && item.itemName) {
            poByBarcode[barcode].itemName = item.itemName;
        }
    });

    const dnByBarcode = {};
    (deliveryNoteItems || []).forEach(item => {
        const barcode = String(item?.barcode || '').trim();
        if (!barcode) {
            return;
        }
        if (!dnByBarcode[barcode]) {
            dnByBarcode[barcode] = {
                barcode,
                itemName: item.itemName || '',
                deliveredQty: 0,
                damagedQty: 0
            };
        }
        const delivered = toQtyNumber(item.actualOrderAmount);
        const damaged = toQtyNumber(item.damagedQuantity);
        dnByBarcode[barcode].deliveredQty += delivered;
        dnByBarcode[barcode].damagedQty += damaged;
        if (!dnByBarcode[barcode].itemName && item.itemName) {
            dnByBarcode[barcode].itemName = item.itemName;
        }
    });

    const barcodes = new Set([...Object.keys(poByBarcode), ...Object.keys(dnByBarcode)]);
    const statusOrder = {UNEXPECTED: 0, MISSING: 1, SHORT: 2, OVER: 3, MATCHED: 4};
    const rows = [];

    barcodes.forEach(barcode => {
        const po = poByBarcode[barcode];
        const dn = dnByBarcode[barcode];
        const orderedQty = po ? po.orderedQty : 0;
        const deliveredQty = dn ? dn.deliveredQty : 0;
        const damagedQty = dn ? dn.damagedQty : 0;
        const netQty = deliveredQty - damagedQty;
        let matchStatus = 'MATCHED';
        let warning = '';

        if (!po && dn) {
            matchStatus = 'UNEXPECTED';
            warning = 'Item was not ordered on Purchase Order';
        } else if (po && !dn) {
            matchStatus = 'MISSING';
            warning = `Ordered ${orderedQty}, nothing received`;
        } else if (deliveredQty < orderedQty) {
            matchStatus = 'SHORT';
            warning = `Ordered ${orderedQty}, received ${deliveredQty}`;
        } else if (deliveredQty > orderedQty) {
            matchStatus = 'OVER';
            warning = `Ordered ${orderedQty}, received ${deliveredQty}`;
        }

        rows.push({
            id: barcode,
            barcode,
            itemName: (dn && dn.itemName) || (po && po.itemName) || '',
            orderedQty,
            deliveredQty,
            damagedQty,
            netQty,
            difference: deliveredQty - orderedQty,
            matchStatus,
            warning
        });
    });

    rows.sort((a, b) => (statusOrder[a.matchStatus] ?? 9) - (statusOrder[b.matchStatus] ?? 9)
        || String(a.barcode).localeCompare(String(b.barcode)));
    return rows;
}

export function countDeliveryPoComparisonIssues(rows) {
    return (rows || []).filter(row => row.matchStatus && row.matchStatus !== 'MATCHED').length;
}

